import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { ApiCallError, callFunction } from '@/lib/api';
import { catalogueDisplayName, catalogueSourceLabel, formatLabDateTime } from '@/lib/lab/labDisplay';
import type { LabCatalogueRow } from '@/lib/lab/labTypes';

export function LabAnalysisCataloguePanel({
  clinicId,
  refreshKey,
  showTitle = true,
}: {
  clinicId: string;
  refreshKey?: number;
  showTitle?: boolean;
}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<LabCatalogueRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await callFunction<Record<string, unknown>, { analyses: LabCatalogueRow[] }>(
        'communicate-lab-import',
        { action: 'catalogue', clinic_id: clinicId },
      );
      setRows(result.analyses);
    } catch (err) {
      setError(err instanceof ApiCallError ? err.message : t('patients.lab.catalogue_load_failed'));
    } finally {
      setLoading(false);
    }
  }, [clinicId, t]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  return (
    <section className="lab-section space-y-3 rounded-md border border-ds-line p-4">
      <div className="flex items-center justify-between gap-2">
        {showTitle ? <h3>{t('patients.lab.catalogue_title')}</h3> : null}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className={showTitle ? undefined : 'ml-auto'}
          onClick={() => void load()}
          disabled={loading}
        >
          {loading ? t('patients.lab.catalogue_refreshing') : t('patients.lab.catalogue_refresh')}
        </Button>
      </div>
      <p className="text-sm text-ds-muted">{t('patients.lab.catalogue_hint')}</p>
      {error && (
        <p role="alert" className="text-sm text-ds-red-ink">
          {error}
        </p>
      )}
      {rows === null && loading && (
        <div className="flex flex-col gap-s3" aria-busy>
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}
      {rows && rows.length === 0 && !loading && (
        <p className="text-sm text-ds-muted">{t('patients.lab.catalogue_empty')}</p>
      )}
      {rows && rows.length > 0 && (
        <table className="lab-table">
          <thead>
            <tr>
              <th>{t('patients.lab.catalogue_code')}</th>
              <th>{t('patients.lab.catalogue_name')}</th>
              <th>{t('patients.lab.catalogue_source')}</th>
              <th>{t('patients.lab.catalogue_created')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const name = catalogueDisplayName(row);
              const short = row.short_name?.trim() ?? '';
              const showShort = Boolean(short && short !== name);
              return (
                <tr key={row.id}>
                  <td>
                    <div>{row.analysis_code}</div>
                    {row.analysis_code_type ? (
                      <div className="text-xs text-ds-muted">{row.analysis_code_type}</div>
                    ) : null}
                  </td>
                  <td>
                    <div>{name}</div>
                    {showShort ? <div className="text-xs text-ds-muted">{short}</div> : null}
                  </td>
                  <td>{catalogueSourceLabel(row.source)}</td>
                  <td>{formatLabDateTime(row.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
